import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./UserPostCreate.module.scss";

const LANGS = ["en", "si", "ta", "hi"];
const VISIBILITIES = ["public", "friends-only"];

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;

const extFromFile = (file) => {
  const name = safeStr(file?.name);
  const dot = name.lastIndexOf(".");
  if (dot === -1) return "";
  return name.slice(dot + 1).toLowerCase();
};

async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";

        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        try {
          URL.revokeObjectURL(el.src || "");
        } catch {
          // Intentionally ignored: object URL cleanup should not block video metadata fallback.
        }
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

const UserPostCreate = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [mode, setMode] = useState("text");
  const [visibility, setVisibility] = useState("public");
  const [lang, setLang] = useState("en");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [publishMode, setPublishMode] = useState("now");
  const [publishAt, setPublishAt] = useState("");

  const [images, setImages] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("");

  const clearMedia = () => {
    setImages([]);
    setVideoFile(null);

    setImagePreviewUrls((prev) => {
      prev.forEach((u) => {
        try {
          if (u) URL.revokeObjectURL(u);
        } catch {
          // Intentionally ignored: object URL cleanup should not block media reset.
        }
      });
      return [];
    });

    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: object URL cleanup should not block media reset.
      }
      return "";
    });
  };

  const putToS3WithProgress = (uploadUrl, file, onPct, contentTypeOverride) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader(
        "Content-Type",
        contentTypeOverride || file?.type || "application/octet-stream",
      );

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        onPct?.(Math.round((evt.loaded / evt.total) * 100));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        reject(new Error(`Upload failed (${xhr.status})`));
      };

      xhr.onerror = () => reject(new Error("Upload failed (network error)"));
      xhr.send(file);
    });

  const presignImagePoster = async (file) => {
    const ext = extFromFile(file) || "jpg";
    const contentType = file?.type || "image/jpeg";
    const res = await api.post("/v1/posts/media/presign-image-poster", {
      ext,
      contentType,
    });
    return res.data;
  };

  const presignVideo = async (file) => {
    const contentType = file?.type || "application/octet-stream";
    const filename =
      safeStr(file?.name) || `video.${extFromFile(file) || "mp4"}`;

    const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
      width: 0,
      height: 0,
      orientation: "landscape",
    };

    const { data } = await api.post("/v1/videos/presign", {
      contentType,
      filename,
      clientMeta,
    });

    return data;
  };

  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remain = Math.max(0, 20 - images.length);
    const toAdd = files.slice(0, remain);

    setImages((prev) => [...prev, ...toAdd].slice(0, 20));
    setImagePreviewUrls((prev) =>
      [...prev, ...toAdd.map((f) => URL.createObjectURL(f))].slice(0, 20),
    );

    e.target.value = "";
  };

  const onPickVideo = (e) => {
    const file = e.target.files?.[0] || null;

    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: object URL cleanup should not block replacing the preview.
      }
      return file ? URL.createObjectURL(file) : "";
    });

    setVideoFile(file);
    e.target.value = "";
  };

  const removeImageAt = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviewUrls((prev) => {
      const target = prev[index];
      try {
        if (target) URL.revokeObjectURL(target);
      } catch {
        // Intentionally ignored: object URL cleanup should not block removing the image.
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearVideo = () => {
    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: object URL cleanup should not block clearing the video.
      }
      return "";
    });
    setVideoFile(null);
  };

  const validate = () => {
    if (!LANGS.includes(lang)) return "Invalid language.";
    if (!VISIBILITIES.includes(visibility)) return "Invalid visibility.";
    if (!["now", "scheduled"].includes(publishMode))
      return "Invalid publish mode.";

    if (publishMode === "scheduled" && !publishAt) {
      return "Schedule time is required.";
    }

    if (mode === "text" && !hasText(title) && !hasText(description)) {
      return "Text post needs title or description.";
    }

    if (mode === "photo" && images.length < 1) {
      return "Photo post needs at least one image.";
    }

    if (mode === "video" && !videoFile) {
      return "Video post needs a video file.";
    }

    return "";
  };

  const submit = async () => {
    setError("");
    setOk("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setLoading(true);
    setUploadPct(0);
    setUploadLabel("");

    try {
      const body = {
        visibility,
        postLanguage: lang,
        title: hasText(title) ? title.trim() : null,
        description: hasText(description) ? description.trim() : null,
        publishMode,
        publishAt: publishMode === "scheduled" ? publishAt : null,
      };

      if (mode === "text") {
        await api.post(`/v1/admin/members/single/${userId}/posts`, {
          ...body,
          type: "text-only",
        });
      }

      if (mode === "photo") {
        const uploaded = [];

        for (let i = 0; i < images.length; i++) {
          const f = images[i];
          setUploadLabel(`Uploading photo ${i + 1}/${images.length}`);
          setUploadPct(0);

          const pres = await presignImagePoster(f);
          await putToS3WithProgress(pres.uploadUrl, f, setUploadPct);
          uploaded.push({ key: pres.processedKey });
        }

        await api.post(`/v1/admin/members/single/${userId}/posts`, {
          ...body,
          type: "photo-post",
          images: uploaded,
        });
      }

      if (mode === "video") {
        setUploadLabel("Uploading video");

        const presign = await presignVideo(videoFile);
        const videoId = safeStr(presign?.id).trim();

        if (!videoId) throw new Error("Server did not return a video id");

        await putToS3WithProgress(
          presign.url,
          videoFile,
          setUploadPct,
          presign.contentType || videoFile?.type,
        );

        const meta = {
          en: { title: "", subtitle: "" },
          si: { title: "", subtitle: "" },
          ta: { title: "", subtitle: "" },
          hi: { title: "", subtitle: "" },
        };

        meta[lang] = {
          title: hasText(title) ? title.trim() : "",
          subtitle: hasText(description) ? description.trim() : "",
        };

        await api.post(`/v1/videos/${videoId}/localize`, { meta });
        await api.post(`/v1/videos/${videoId}/submit`);

        await api.post(`/v1/admin/members/single/${userId}/posts`, {
          ...body,
          type: "video-post",
          videoId,
        });
      }

      setOk("Post created successfully.");
      clearMedia();
      setTitle("");
      setDescription("");
      setPublishMode("now");
      setPublishAt("");
      setMode("text");
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to create post",
      );
    } finally {
      setLoading(false);
      setUploadLabel("");
      setUploadPct(0);
    }
  };

  const submitText = useMemo(() => {
    if (!loading) return "Create Post";
    if (!uploadLabel) return "Working...";
    if (uploadPct > 0) return `${uploadLabel} (${uploadPct}%)`;
    return uploadLabel;
  }, [loading, uploadLabel, uploadPct]);

  const modeNote = useMemo(() => {
    if (mode === "text") {
      return "Create a clean text-based post using title or description.";
    }
    if (mode === "photo") {
      return "Upload up to 20 photos and publish them as a member post.";
    }
    return "Upload one video and publish it with localized title and subtitle.";
  }, [mode]);

  return (
    <div className={cls.page}>
      <div className={cls.bgGlowOne} />
      <div className={cls.bgGlowTwo} />

      <header className={cls.topbar}>
        <div className={cls.topbarLeft}>
          <button
            type="button"
            className={cls.backBtn}
            onClick={() => navigate(`/admin/members/single/posts/${userId}`)}
          >
            <span className={cls.btnIcon}>←</span>
            <span>Back to Posts</span>
          </button>

          <div className={cls.titleWrap}>
            <p className={cls.eyebrow}>Admin Post Creator</p>
            <h1 className={cls.pageTitle}>Create Post as Member</h1>
          </div>
        </div>
      </header>

      <section className={`${cls.card} ${cls.heroCard}`}>
        <div className={cls.heroTop}>
          <div>
            <p className={cls.sectionEyebrow}>Post Setup</p>
            <h2 className={cls.sectionTitle}>Choose Post Type</h2>
            <p className={cls.heroNote}>{modeNote}</p>
          </div>

          <div className={cls.memberPill}>
            <span className={cls.memberDot} />
            <span>Member ID: {userId}</span>
          </div>
        </div>

        <div className={cls.modeRow}>
          <button
            type="button"
            className={`${cls.modeBtn} ${cls.modeText} ${
              mode === "text" ? cls.active : ""
            }`}
            onClick={() => setMode("text")}
            disabled={loading}
          >
            <span className={cls.modeIcon}>✍️</span>
            <span className={cls.modeLabel}>Text</span>
          </button>

          <button
            type="button"
            className={`${cls.modeBtn} ${cls.modePhoto} ${
              mode === "photo" ? cls.active : ""
            }`}
            onClick={() => setMode("photo")}
            disabled={loading}
          >
            <span className={cls.modeIcon}>🖼️</span>
            <span className={cls.modeLabel}>Photo</span>
          </button>

          <button
            type="button"
            className={`${cls.modeBtn} ${cls.modeVideo} ${
              mode === "video" ? cls.active : ""
            }`}
            onClick={() => setMode("video")}
            disabled={loading}
          >
            <span className={cls.modeIcon}>🎬</span>
            <span className={cls.modeLabel}>Video</span>
          </button>
        </div>
      </section>

      <section className={cls.card}>
        <div className={cls.cardHead}>
          <div>
            <p className={cls.sectionEyebrow}>Content Settings</p>
            <h2 className={cls.sectionTitle}>Post Details</h2>
          </div>
        </div>

        <div className={cls.grid}>
          <div className={cls.field}>
            <label className={cls.label}>Visibility</label>
            <select
              className={cls.select}
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              disabled={loading}
            >
              {VISIBILITIES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div className={cls.field}>
            <label className={cls.label}>Language</label>
            <select
              className={cls.select}
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              disabled={loading}
            >
              {LANGS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div className={cls.field}>
            <label className={cls.label}>Publish Mode</label>
            <select
              className={cls.select}
              value={publishMode}
              onChange={(e) => setPublishMode(e.target.value)}
              disabled={loading}
            >
              <option value="now">now</option>
              <option value="scheduled">scheduled</option>
            </select>
          </div>

          {publishMode === "scheduled" && (
            <div className={cls.field}>
              <label className={cls.label}>Schedule At</label>
              <input
                className={cls.input}
                type="datetime-local"
                value={publishAt}
                onChange={(e) => setPublishAt(e.target.value)}
                disabled={loading}
              />
            </div>
          )}
        </div>

        <div className={cls.field}>
          <label className={cls.label}>
            Title <span className={cls.count}>Max 160</span>
          </label>
          <input
            className={cls.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={160}
            disabled={loading}
            placeholder="Write a title for this post"
          />
        </div>

        <div className={cls.field}>
          <label className={cls.label}>
            Description <span className={cls.count}>Max 4000</span>
          </label>
          <textarea
            className={cls.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            maxLength={4000}
            disabled={loading}
            placeholder="Write the post description"
          />
        </div>

        {mode === "photo" && (
          <div className={cls.field}>
            <div className={cls.fieldTop}>
              <label className={cls.label}>Photos</label>
              <span className={cls.helper}>{images.length}/20 selected</span>
            </div>

            <label className={cls.uploadBox}>
              <input
                className={cls.fileInput}
                type="file"
                accept="image/*"
                multiple
                onChange={onPickImages}
                disabled={loading}
              />
              <span className={cls.uploadIcon}>📸</span>
              <span className={cls.uploadTitle}>Click to choose photos</span>
              <span className={cls.uploadSub}>
                You can upload up to 20 images
              </span>
            </label>

            {!!imagePreviewUrls.length && (
              <div className={cls.previewGrid}>
                {imagePreviewUrls.map((url, i) => (
                  <div className={cls.previewCard} key={`${url}-${i}`}>
                    <img src={url} alt={`preview-${i + 1}`} />
                    <button
                      type="button"
                      className={cls.removeBtn}
                      onClick={() => removeImageAt(i)}
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === "video" && (
          <div className={cls.field}>
            <div className={cls.fieldTop}>
              <label className={cls.label}>Video</label>
              {videoFile && (
                <button
                  type="button"
                  className={cls.clearMediaBtn}
                  onClick={clearVideo}
                  disabled={loading}
                >
                  Clear Video
                </button>
              )}
            </div>

            <label className={cls.uploadBox}>
              <input
                className={cls.fileInput}
                type="file"
                accept="video/*"
                onChange={onPickVideo}
                disabled={loading}
              />
              <span className={cls.uploadIcon}>🎥</span>
              <span className={cls.uploadTitle}>Click to choose video</span>
              <span className={cls.uploadSub}>
                Upload one video file for this post
              </span>
            </label>

            {videoPreviewUrl && (
              <div className={cls.videoWrap}>
                <video className={cls.video} src={videoPreviewUrl} controls />
              </div>
            )}
          </div>
        )}

        {loading && !!uploadLabel && (
          <div className={cls.progressWrap}>
            <div className={cls.progressHead}>
              <span>{uploadLabel}</span>
              <strong>{uploadPct}%</strong>
            </div>
            <div className={cls.progressBar}>
              <span style={{ width: `${uploadPct}%` }} />
            </div>
          </div>
        )}

        {error && <p className={cls.error}>{error}</p>}
        {ok && <p className={cls.ok}>{ok}</p>}

        <div className={cls.actions}>
          <button
            type="button"
            className={cls.submitBtn}
            onClick={submit}
            disabled={loading}
          >
            {submitText}
          </button>
        </div>
      </section>
    </div>
  );
};

export default UserPostCreate;
